import React, { useState, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFaq,
  deleteFaq,
  getFaqDetail,
  listFaqs,
  updateFaq,
  FaqFormValues,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";

export const useFaqList = (qData?: QData) => {
  const [extraQuery, setExtraQuery] = useState<Record<string, any>>({});

  const mergedQData = useMemo(() => {
    return { ...qData, ...extraQuery };
  }, [qData, extraQuery]);

  const queryResult = useQuery({
    queryKey: ["/faq", mergedQData],
    queryFn: async () => {
      const res = await listFaqs(mergedQData);
      return res.data;
    },
  });

  const refetchWithQuery = useCallback((newQData: QData) => {
    setExtraQuery(newQData);
  }, []);

  return {
    ...queryResult,
    refetchWithQuery,
  };
};

export const useFaqCreate = ({
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FaqFormValues) => createFaq(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/faq"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useFaqUpdate = ({
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FaqFormValues> }) =>
      updateFaq({ id, data }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/faq"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useFaqDelete = ({
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteFaq(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/faq"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useFaqDetail = (id: string, options = {}) => {
  return useQuery({
    queryKey: ["/faq", id],
    queryFn: async () => {
      const res = await getFaqDetail(id);
      return res.data;
    },
    enabled: !!id,
    ...options,
  });
};
